import { type ReactNode } from "react";

import { BTDrawer, type IBTDrawerProps } from "../BTDrawer/BTDrawer";
import { useBottomSheet } from "../BTDrawer/useBottomSheet";

export interface IBTBottomSheetProps
  extends Pick<
    IBTDrawerProps,
    | "visible"
    | "onClose"
    | "destroyOnClose"
    | "zIndex"
    | "keyboard"
    | "role"
    | "aria-label"
    | "afterVisibleChange"
  > {
  children: ReactNode;
  /** Optional class name for the bottom sheet */
  className?: string;
  /**
   * Open at the sheet's full height rather than sizing to the content. Turn it on for content
   * that expects to be given a height — a scrolling list, a viewer, anything that renders
   * before its data arrives.
   * @default false
   */
  fillHeight?: boolean;
  "data-testid": string;
}

/**
 * A mobile-optimized bottom sheet component that slides up from the bottom.
 * Wraps BTDrawer with iOS-style bottom sheet styling including:
 * - Rounded top corners
 * - Height that follows the content up to a maximum, or the full height via `fillHeight`
 *
 * The behavior itself lives in `useBottomSheet`; this is the ready-made component for the
 * common case of "I want a bottom sheet". A caller that renders its own `BTDrawer` and
 * switches it between presentations should call that hook directly instead — see `BTPeek`.
 */
export function BTBottomSheet({
  children,
  visible,
  onClose,
  className,
  destroyOnClose = true,
  role = "dialog",
  fillHeight = false,
  // The drag handle is decorative and contributes no name, so a role="dialog" with no heading
  // of its own would announce as unlabelled. Sheets that render their own heading can leave
  // this alone; the fallback only applies when nothing else names the sheet.
  "aria-label": ariaLabel = "Bottom sheet",
  "data-testid": testId,
  ...otherProps
}: IBTBottomSheetProps) {
  const sheet = useBottomSheet({
    visible,
    onClose,
    fillHeight,
    className,
  });

  return (
    <BTDrawer
      {...sheet.drawerProps}
      visible={visible ?? false}
      onClose={onClose}
      destroyOnClose={destroyOnClose}
      role={role}
      aria-label={ariaLabel}
      {...otherProps}
    >
      {sheet.handle}
      <div className={sheet.bodyClassName} data-testid={`${testId}-body`}>
        {children}
      </div>
    </BTDrawer>
  );
}
